import { CircleAlert } from 'lucide-react';
import type { wave } from '@round/api';
import type { OfferPost } from '@/utility/offerPosts';
import {
    countOffers,
    getCardPostCaps,
    getPayoutUnlocks,
    getReconsiderableContractId,
    getSharedPayoutUnlocks,
    sortContracts,
} from '@/utility/contracts';
import ContractCard from './ContractCard';
import type { ContractResponse } from './ContractCard';
import OfferPosts from './OfferPosts';

type Props = {
    contracts: wave.CreatorContract[];
    invite: wave.Invite;
    nowMs: number;
    /** The invite's posts, shown under the offer each counts toward. */
    posts: OfferPost[];
    onRespond?: (contract: wave.CreatorContract, answer: ContractResponse) => Promise<boolean>;
    onSubmitPost?: (contract: wave.CreatorContract, postUrl: string) => Promise<string | null>;
    declineReasons?: string[];
};

const SUPPORT_EMAIL = 'hello@wave.me';

const getCardAccounts = (contract: wave.Contract, inviteAccounts: wave.InviteAccount[]): wave.InviteAccount[] =>
    contract.allowed_accounts.map(
        (account) =>
            inviteAccounts.find((inviteAccount) => inviteAccount.id === account.id) ?? {
                ...account,
                avatar_url: null,
                profile_url: null,
            }
    );

const ContractList = ({ contracts, invite, nowMs, posts, onRespond, onSubmitPost, declineReasons }: Props) => {
    // Every sent invite carries a contract, so none means something went wrong on our side.
    if (contracts.length === 0) {
        return (
            <section className="submission-limit-notice">
                <CircleAlert size={16} />
                <div>
                    <h2>We couldn&apos;t find your offer</h2>
                    <p>Get in touch at {SUPPORT_EMAIL} and we&apos;ll sort it out.</p>
                </div>
            </section>
        );
    }
    const reconsiderableId = getReconsiderableContractId(contracts);
    const postCaps = getCardPostCaps(contracts);
    const hasSeveralOffers = countOffers(contracts) > 1;
    const hasMixedPayouts = getSharedPayoutUnlocks(contracts) === null;
    return (
        <section className="public-section contract-list" aria-label="Your offers">
            <h2>{hasSeveralOffers ? 'Your offers' : 'Your offer'}</h2>
            {hasSeveralOffers ? (
                <p>Each offer has its own terms and posting window. Submit a post under the offer it counts toward.</p>
            ) : null}
            <div className="contract-list__items">
                {sortContracts(contracts).map((contract) => {
                    const offerPosts = posts.filter((post) => post.contractId === contract.id);
                    return (
                        <ContractCard
                            key={contract.id}
                            contract={contract}
                            accounts={getCardAccounts(contract, invite.allowed_accounts)}
                            postCap={postCaps.get(contract.id)}
                            platform={invite.platform}
                            nowMs={nowMs}
                            canReconsider={contract.id === reconsiderableId}
                            onRespond={onRespond}
                            onSubmitPost={onSubmitPost}
                            declineReasons={declineReasons}
                            payoutUnlocks={hasMixedPayouts ? getPayoutUnlocks(contract) : undefined}
                        >
                            {offerPosts.length > 0 ? <OfferPosts contract={contract} posts={offerPosts} /> : null}
                        </ContractCard>
                    );
                })}
            </div>
        </section>
    );
};

export default ContractList;
